import { z } from 'zod';

export const PROPOSAL_STATUSES = [
  'draft',
  'sent',
  'viewed',
  'accepted',
  'rejected',
  'expired',
] as const;
export const proposalStatusEnum = z.enum(PROPOSAL_STATUSES);
export type ProposalStatus = z.infer<typeof proposalStatusEnum>;

const proposalItemSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(160),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  quantity: z.coerce.number().min(0).default(1),
  unitPrice: z.coerce.number().min(0).default(0),
  /** Percentage, 0–100 — applied per line item before the proposal-level discount/tax. */
  discount: z.coerce.number().min(0).max(100).default(0),
});

export type ProposalItemInput = z.infer<typeof proposalItemSchema>;

/**
 * A priced quote sent to a lead or customer — deferred from the CRM
 * module's core pass, ported now from Office_project's `Proposal` model.
 * `subtotal`/`discountAmount`/`taxAmount`/`total` are always server-computed
 * (see `proposal.service.ts`'s `calculateTotals`), never accepted from the
 * client, so they're intentionally absent from this input schema.
 */
export const createProposalSchema = z.object({
  title: z.string().trim().min(2, 'Title must be at least 2 characters').max(200),

  leadId: z.string().optional(),
  customerId: z.string().optional(),
  opportunityId: z.string().optional(),

  items: z.array(proposalItemSchema).default([]),
  discountPercent: z.coerce.number().min(0).max(100).default(0),
  taxRate: z.coerce.number().min(0).max(100).default(18),

  validUntil: z.coerce.date().optional(),
  notes: z.string().trim().max(2000).optional().or(z.literal('')),
  terms: z.string().trim().max(2000).optional().or(z.literal('')),

  assignedTo: z.string().optional(),
});

export type CreateProposalInput = z.infer<typeof createProposalSchema>;

export const updateProposalSchema = createProposalSchema.partial();
export type UpdateProposalInput = z.infer<typeof updateProposalSchema>;

export const updateProposalStatusSchema = z.object({
  status: proposalStatusEnum,
});

export type UpdateProposalStatusInput = z.infer<typeof updateProposalStatusSchema>;

export const proposalListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: proposalStatusEnum.optional(),
  leadId: z.string().optional(),
  customerId: z.string().optional(),
});

export type ProposalListQuery = z.infer<typeof proposalListQuerySchema>;
